// :)
/**
 * `synkro sync` — pull the registry packs a repo subscribes to, verify them,
 * and apply.
 *
 * Reads the [standards] table from synkro.toml, resolves each ref against the
 * gateway, VERIFIES the Ed25519 signature + content digest locally (nothing
 * binds unless it passes), pins the result in synkro.lock, and materializes:
 *   • local  → verified pack JSON in ~/.synkro/cache/packs/ (read by the
 *     container's readRules; the repo tree stays clean — toml + lock only).
 *   • cloud  → lockfile only; mandatory/subscribed packs are injected
 *     server-side at grade time (Timescale). local → pglite / cloud → timescale.
 */

import { existsSync, mkdirSync, readdirSync, rmSync, writeFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { readFullSynkroFile } from './install.js';
import { ensureValidToken, getAccessToken } from '../auth/stub.js';
import { verifyPack, type PackContent } from '../installer/packVerify.js';
import { writeLockfile, type LockEntry } from '../installer/lockfile.js';

interface ResolveResponse extends PackContent {
  ref: string;
  version: string;
  digest: string;
  signature: string;
  signingKeyId: string;
  publicKey: string;
}

function cacheKey(ref: string, version: string): string {
  return ref.replace(/\//g, '__').replace(/[^\w.@-]/g, '_') + '@' + version + '.json';
}

export async function syncCommand(_args: string[] = []): Promise<void> {
  // Cloud mode applies standards 100% server-side: org-mandatory packs (all
  // repos or selected) are enforced at the harness by org, with no synkro.toml.
  // `synkro sync` is a local-only concept — there's nothing to pull on cloud.
  if (process.env.SYNKRO_DEPLOY_LOCATION === 'cloud') {
    console.log('Cloud mode: standards are applied org-wide from the dashboard');
    console.log('(Settings → Standards). No synkro.toml or `synkro sync` needed —');
    console.log('packs enforce automatically wherever Synkro is installed.');
    return;
  }

  const sf = readFullSynkroFile();
  if (!sf) {
    console.error('No synkro.toml found in the repo root. Run `synkro install` first.');
    process.exitCode = 1;
    return;
  }

  const refs = Object.entries(sf.standards);
  if (refs.length === 0) {
    console.log('No [standards] subscriptions in synkro.toml — nothing to sync.');
    return;
  }

  await ensureValidToken();
  const token = getAccessToken();
  if (!token) {
    console.error('Not logged in. Run `synkro login` and try again.');
    process.exitCode = 1;
    return;
  }

  const gateway = (process.env.SYNKRO_GATEWAY_URL || 'https://api.synkro.sh').replace(/\/$/, '');
  const cloud = process.env.SYNKRO_DEPLOY_LOCATION === 'cloud';
  const cacheDir = join(homedir(), '.synkro', 'cache', 'packs');
  if (!cloud) mkdirSync(cacheDir, { recursive: true });

  console.log(`Syncing ${refs.length} standard(s) from the registry…`);
  const lock: LockEntry[] = [];
  const keptCacheFiles = new Set<string>();

  for (const [ref, version] of refs) {
    let url = `${gateway}/api/v1/registry/resolve?ref=${encodeURIComponent(ref)}`;
    if (version && version !== 'latest') url += `&version=${encodeURIComponent(version)}`;
    let data: ResolveResponse;
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        console.error(`  ✗ ${ref}: resolve failed (${resp.status} ${resp.statusText})`);
        continue;
      }
      data = await resp.json() as ResolveResponse;
    } catch (err) {
      console.error(`  ✗ ${ref}: ${(err as Error).message}`);
      continue;
    }

    const pack: PackContent = { rules: data.rules ?? [], docs: data.docs ?? [], manifest: data.manifest ?? {} };
    if (!verifyPack(pack, data.digest, data.signature, data.publicKey)) {
      console.error(`  ✗ ${ref}: signature/digest verification FAILED — refusing to apply.`);
      continue;
    }

    lock.push({ ref, version: data.version, digest: data.digest, signature: data.signature, signingKeyId: data.signingKeyId });

    if (!cloud) {
      const fname = cacheKey(ref, data.version);
      keptCacheFiles.add(fname);
      writeFileSync(join(cacheDir, fname), JSON.stringify({
        ref, version: data.version, digest: data.digest,
        rules: pack.rules, docs: pack.docs, manifest: pack.manifest,
      }), 'utf-8');
    }

    const ruleCount = Array.isArray(pack.rules) ? pack.rules.length : 0;
    console.log(`  ✓ ${ref}:${data.version} — verified (${ruleCount} rule${ruleCount === 1 ? '' : 's'})`);
  }

  // Local: drop cache packs no longer subscribed so a removed [standards] line
  // actually stops enforcing. (Synced packs are read-only here — edit upstream
  // and re-publish, never hand-edit the cache.)
  if (!cloud && existsSync(cacheDir)) {
    for (const f of readdirSync(cacheDir)) {
      if (f.endsWith('.json') && !keptCacheFiles.has(f)) {
        try { rmSync(join(cacheDir, f)); } catch {}
      }
    }
  }

  if (lock.length > 0) {
    writeLockfile(sf._repoRoot, lock);
    console.log(`Wrote synkro.lock (${lock.length} pack${lock.length === 1 ? '' : 's'} pinned).`);
  } else {
    console.error('No packs synced successfully.');
    process.exitCode = 1;
  }
}
